/*#
# The transcript

A card is a document: open it and you get the full record — body, comments,
attachments, tag changes, adapter activity — interleaved chronologically and
attributed. `transcript()` is a *reader* over the task's events, nothing more.

Surfaced progressively: by default **salient** events (comments, attachments,
closes) stand alone and **mechanical** runs (tag churn, edit stamps,
subscriptions) fold into one expandable group with a label like
"3 tag changes, 1 edit". Pass `{ fold: false }` for the unfolded stream —
which is what the agent API always gets.
*/

/*{ "parent": "Reference", "order": 50 }*/

import {
  SALIENCE,
  textHash,
  type Correction,
  type EventKind,
  type VirtaEvent,
} from './model'

/** A comment its author took back: when, and why (board #759). */
export interface Retraction {
  at: string
  by: string
  reason: string
}

export type TranscriptEntry =
  | { type: 'event'; id: string; event: VirtaEvent; retracted?: Retraction }
  | { type: 'group'; id: string; label: string; events: VirtaEvent[] }

export interface TranscriptOptions {
  /** Fold runs of mechanical events into groups. Default true. */
  fold?: boolean
  /** A run shorter than this is shown unfolded. Default 2. */
  minGroup?: number
}

const NOUNS: Record<EventKind, [string, string]> = {
  created: ['creation', 'creations'],
  retitled: ['retitle', 'retitles'],
  edited: ['edit', 'edits'],
  tagged: ['tag change', 'tag changes'],
  untagged: ['tag change', 'tag changes'],
  commented: ['comment', 'comments'],
  retracted: ['retraction', 'retractions'],
  attached: ['attachment', 'attachments'],
  showcased: ['showcase', 'showcases'],
  measured: ['measure', 'measures'],
  procedure: ['procedure', 'procedures'],
  suggested: ['suggestion', 'suggestions'],
  practiced: ['practice change', 'practice changes'],
  resolved: ['resolution', 'resolutions'],
  closed: ['close', 'closes'],
  reopened: ['reopen', 'reopens'],
  subscribed: ['subscription change', 'subscription changes'],
  unsubscribed: ['subscription change', 'subscription changes'],
  project: ['settings change', 'settings changes'],
  reattributed: ['attribution correction', 'attribution corrections'],
}

/** "3 tag changes, 1 edit" */
export function groupLabel(events: readonly VirtaEvent[]): string {
  const counts = new Map<string, number>()
  for (const e of events) {
    const noun = NOUNS[e.kind]
    counts.set(noun[0], (counts.get(noun[0]) ?? 0) + 1)
  }
  return Array.from(counts, ([singular, n]) => {
    const kind = (Object.keys(NOUNS) as EventKind[]).find(
      (k) => NOUNS[k][0] === singular
    )!
    return `${n} ${n === 1 ? singular : NOUNS[kind][1]}`
  }).join(', ')
}

/**
 * Whether a correction may speak for an event (board #2752): only the same
 * credential (the correction was stamped with the event's token) or its
 * principal signed in directly (same uid, no token). The host's stamps are
 * unforgeable, so a correction is too. A local log has no stamps: there a
 * correction applies only to unstamped events.
 */
function honoured(c: Correction, own: VirtaEvent['attribution']): boolean {
  if (!c.stamp) return !own
  if (!own) return false
  if (c.stamp.token) return c.stamp.token === own.token
  return c.stamp.uid === own.uid
}

/** Whether an honoured correction names this event. */
function names(c: Correction, event: VirtaEvent): boolean {
  const own = event.attribution
  const named = c.events
    ? c.events.includes(event.id)
    : !!c.token &&
      own?.token === c.token &&
      (!c.through || event.at <= c.through)
  return named && honoured(c, own)
}

/**
 * The corrections that apply to an event: the latest honoured one that
 * sets the label, and the latest that sets the agent part (board #2830) —
 * a range can fix the label while a list fixes the agent of a few writes.
 */
export function correctionsFor(
  event: VirtaEvent,
  corrections: readonly Correction[]
): { label: Correction | null; agent: Correction | null } {
  let label: Correction | null = null
  let agent: Correction | null = null
  for (let i = corrections.length - 1; i >= 0 && !(label && agent); i--) {
    const c = corrections[i]!
    if (!names(c, event)) continue
    if (!label && c.label) label = c
    if (!agent && c.agent) agent = c
  }
  return { label, agent }
}

/** The latest correction that applies to an event, either kind. */
export function correctionFor(
  event: VirtaEvent,
  corrections: readonly Correction[]
): Correction | null {
  for (let i = corrections.length - 1; i >= 0; i--)
    if (names(corrections[i]!, event)) return corrections[i]!
  return null
}

/**
 * Who wrote an event, for display: the host's attribution (the credential —
 * unforgeable) when there is one, and when the writer named itself
 * differently (an import keeps the original author), both: `github:ada via
 * Tosi × tosijs-virta`. A local log has only the writer's word (`by`). The
 * board and the CLI both use this, so they cannot disagree.
 */
export function whoWrote(
  event: VirtaEvent,
  corrections: readonly Correction[] = []
): string {
  const fix = correctionsFor(event, corrections)
  if (fix.label || fix.agent) {
    // the corrected label in place of the token's, and `by`'s parts with
    // it — its machine part was derived from the label, its agent part
    // from the folder the command ran in; the original stays in view
    const was = event.attribution?.label ?? ''
    const cut = event.by.indexOf(' × ')
    const machine = cut < 0 ? event.by : event.by.slice(0, cut)
    const agentPart = cut < 0 ? '' : event.by.slice(cut + 3)
    const newLabel = fix.label?.label ?? was
    const newMachine = fix.label && was && machine === was ? newLabel : machine
    const newAgent = fix.agent?.agent ?? agentPart
    const by = newAgent ? `${newMachine} × ${newAgent}` : newMachine
    const from = [
      fix.label && was !== newLabel ? was || 'no label' : '',
      fix.agent && agentPart !== newAgent ? agentPart || 'no agent' : '',
    ].filter(Boolean)
    const shown = whoWrote({
      ...event,
      by,
      attribution: event.attribution
        ? { ...event.attribution, label: newLabel }
        : undefined,
    })
    return from.length ? `${shown} (corrected from ${from.join(' × ')})` : shown
  }
  const label = event.attribution?.label || event.attribution?.name
  if (!label) return event.by
  if (!event.by || event.by === label) return label
  // an agent's token names its machine (\`Tosi\`), and \`by\` adds the repo it
  // acts for (\`Tosi × tosijs-3d\`): when \`by\` extends the verified label,
  // it says it all (board #171). Anything else shows both.
  if (event.attribution?.token && event.by.startsWith(`${label} × `))
    return event.by
  return `${event.by} via ${label}`
}

/** One line describing an event, for the transcript and for agents. */
export function describeEvent(event: VirtaEvent): string {
  switch (event.kind) {
    case 'created':
      return `created "${event.payload.title}"`
    case 'retitled':
      return `retitled to "${event.payload.title}"`
    case 'edited':
      // what the writer said changed (a skill's publish note, board #2477)
      return event.payload.note
        ? `edited the body: ${event.payload.note}`
        : 'edited the body'
    case 'tagged':
      return `tagged ${event.payload.tag}`
    case 'untagged':
      return `untagged ${event.payload.tag}`
    case 'commented':
      return event.payload.text
    case 'retracted':
      return event.payload.undo ? 'restored a comment' : 'retracted a comment'
    case 'attached':
      return `attached ${event.payload.name}`
    case 'procedure':
      return `procedure: when ${
        event.payload.when || 'any task'
      } → set ${event.payload.set.join(' ')}`
    case 'suggested':
      return event.payload.text
    case 'practiced':
      return `a practice ${[
        ...event.payload.added.map((t) => `set ${t}`),
        ...event.payload.removed.map((t) => `removed ${t}`),
      ].join(', ')}`
    case 'resolved':
      return `${event.payload.outcome} a practice's ${
        event.payload.outcome === 'undone' ? 'change' : 'suggestion'
      }`
    case 'measured':
      return `measured by: ${event.payload.measure} — retire if: ${event.payload.death}`
    case 'showcased':
      return event.payload.withdrawn
        ? 'withdrew the showcase'
        : `showcased "${event.payload.title}"`
    case 'closed':
      return event.payload.reason ? `closed: ${event.payload.reason}` : 'closed'
    case 'reopened':
      return 'reopened'
    case 'subscribed':
      return `${event.payload.subscriber} subscribed`
    case 'unsubscribed':
      return `${event.payload.subscriber} unsubscribed`
    case 'project': {
      const keys = Object.keys(event.payload.settings)
      return `set ${event.payload.name} ${keys.join(', ') || 'settings'}`
    }
    case 'reattributed': {
      const p = event.payload
      const what = p.events?.length
        ? `${p.events.length} event(s)`
        : p.through
        ? `what token ${p.token ?? '?'} wrote through ${p.through.slice(0, 16)}`
        : `everything token ${p.token ?? '?'} writes`
      const to = [
        p.label ? `"${p.label}"` : '',
        p.agent ? `agent ${p.agent}` : '',
      ]
        .filter(Boolean)
        .join(', ')
      return `credited ${what} to ${to}${p.reason ? `: ${p.reason}` : ''}`
    }
  }
}

/**
 * Who wrote an event, as far as a retraction is concerned: the host's
 * verified uid (when there is one) and the writer's word. A retraction
 * counts only when it matches its comment's author — so nobody else can
 * take back your words, and a forged attempt is visible and has no effect.
 */
const authorOf = (e: VirtaEvent): string =>
  `${e.attribution?.uid ?? ''}|${e.by}`

/** Which comments are retracted now, in log order, honouring only their authors. */
export function retractions(
  events: readonly VirtaEvent[]
): Map<string, Retraction> {
  const comments = new Map<string, VirtaEvent>()
  const out = new Map<string, Retraction>()
  for (const e of events) {
    if (e.kind === 'commented') comments.set(e.id, e)
    if (e.kind !== 'retracted') continue
    const { commentId, reason, undo } = e.payload as {
      commentId: string
      reason: string
      undo?: boolean
    }
    const comment = comments.get(commentId)
    if (!comment || authorOf(comment) !== authorOf(e)) continue
    if (undo) out.delete(commentId)
    else out.set(commentId, { at: e.at, by: e.by, reason })
  }
  return out
}

export function transcript(
  events: readonly VirtaEvent[],
  options: TranscriptOptions = {}
): TranscriptEntry[] {
  const { fold = true, minGroup = 2 } = options
  const retracted = retractions(events)
  const out: TranscriptEntry[] = []
  let run: VirtaEvent[] = []
  const flush = () => {
    if (run.length >= minGroup) {
      out.push({
        type: 'group',
        id: `g${run[0]!.seq}`,
        label: groupLabel(run),
        events: run,
      })
    } else {
      for (const event of run) out.push(entryFor(event))
    }
    run = []
  }
  for (const event of events) {
    if (fold && SALIENCE[event.kind] === 'mechanical') {
      run.push(event)
    } else {
      flush()
      out.push(entryFor(event))
    }
  }
  flush()
  return out

  function entryFor(event: VirtaEvent): TranscriptEntry {
    const r = retracted.get(event.id)
    return r
      ? { type: 'event', id: event.id, event, retracted: r }
      : { type: 'event', id: event.id, event }
  }
}

/**
 * A line diff (board #2473): the lines of `after`, and those of `before`
 * that went, each marked `+ `, `- ` or `  `. Longest common subsequence —
 * a body is small.
 */
export function lineDiff(before: string, after: string): string[] {
  const a = before.split('\n')
  const b = after.split('\n')
  const n = a.length
  const m = b.length
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0)
  )
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      lcs[i]![j] =
        a[i] === b[j]
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!)
  const out: string[] = []
  let i = 0
  let j = 0
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      out.push(`  ${a[i]}`)
      i++
      j++
    } else if (i < n && (j >= m || lcs[i + 1]![j]! >= lcs[i]![j + 1]!)) {
      // what went, before what replaced it
      out.push(`- ${a[i]}`)
      i++
    } else {
      out.push(`+ ${b[j]}`)
      j++
    }
  }
  return out
}

/** One version of a body, and what changed from the one before. */
export interface BodyVersion {
  version: number
  at: string
  by: string
  body: string
  /** the line diff from the previous version (none for the first) */
  diff: string[]
  /** made from an older version: kept beside the body, not in it (board #2473) */
  concurrent: boolean
}

/**
 * Every version of a task's body, oldest first (board #2473): the body it
 * was created with, then each edit. A concurrent edit is listed too,
 * marked, diffed against the version it was made from.
 */
export function bodyVersions(
  events: readonly VirtaEvent[],
  // who wrote each, as the board shows it (`store.whoWrote`: the corrected label)
  who: (e: VirtaEvent) => string = (e) => whoWrote(e)
): BodyVersion[] {
  const out: BodyVersion[] = []
  let current = ''
  const byHash = new Map<string, string>()
  for (const e of events) {
    if (e.kind === 'created') {
      current = e.payload.body
      byHash.set(textHash(current), current)
      out.push({
        version: 1,
        at: e.at,
        by: who(e),
        body: current,
        diff: [],
        concurrent: false,
      })
    } else if (e.kind === 'edited') {
      const base = e.payload.base
      const concurrent =
        base !== undefined &&
        base !== textHash(current) &&
        e.payload.body !== current
      const from = concurrent ? byHash.get(base!) ?? current : current
      out.push({
        version: out.length + 1,
        at: e.at,
        by: who(e),
        body: e.payload.body,
        diff: lineDiff(from, e.payload.body),
        concurrent,
      })
      if (!concurrent) {
        current = e.payload.body
        byHash.set(textHash(current), current)
      }
    }
  }
  return out
}
